import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    useTheme,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Grid
} from '@mui/material';
import { tokens } from '../../../theme';
import api from '../../../actions/axios';

const ExportEmailsDialog = ({ open, onClose, planFilter }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [emailCount, setEmailCount] = useState(1000);
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = most active first, 'asc' = least active first
    const [activityPeriod, setActivityPeriod] = useState('30d'); // Time period for activity calculation
    const [activityType, setActivityType] = useState('all'); // all, active_only, inactive_only
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [error, setError] = useState('');
    const [previewData, setPreviewData] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);

    // Fetch preview data when dialog opens or filters change
    useEffect(() => {
        if (open) {
            fetchPreview();
        }
    }, [open, sortOrder, planFilter, activityPeriod, activityType]);

    const fetchPreview = async () => {
        setPreviewLoading(true);
        setError('');
        
        try {
            const response = await api.get(
                `/api/v1/admin/dashboard2/preview-emails?limit=100&sortOrder=${sortOrder}&plan=${planFilter}&activityPeriod=${activityPeriod}&activityType=${activityType}`
            );
            
            setPreviewData(response.data.users || []);
            setTotalUsers(response.data.totalCount || 0);
        } catch (err) {
            console.error('Preview fetch failed:', err);
            setError('Failed to load preview data');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleExport = async () => {
        // Validate input
        if (!emailCount || emailCount < 1 || emailCount > 100000) {
            setError('Please enter a number between 1 and 100,000');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await api.get(
                `/api/v1/admin/dashboard2/export-emails?limit=${emailCount}&sortOrder=${sortOrder}&plan=${planFilter}&activityPeriod=${activityPeriod}&activityType=${activityType}`,
                {
                    responseType: 'blob'
                }
            );

            // Create a blob from the response
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            
            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            const sortLabel = sortOrder === 'desc' ? 'most_active' : 'least_active';
            const activityLabel = activityType === 'active_only' ? 'active' : activityType === 'inactive_only' ? 'inactive' : 'all';
            link.download = `emails_${activityLabel}_${sortLabel}_${activityPeriod}_${emailCount}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // Close dialog on success
            onClose();
        } catch (err) {
            console.error('Export failed:', err);
            setError('Failed to export emails. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading && !previewLoading) {
            setError('');
            setPreviewData([]);
            onClose();
        }
    };

    const formatLastActive = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
        return `${Math.floor(diffDays / 365)} years ago`;
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle sx={{ bgcolor: colors.primary[400] }}>
                Export Emails for Newsletter
            </DialogTitle>
            <DialogContent sx={{ bgcolor: colors.primary[400], pt: 3 }}>
                <Box>
                    <Typography variant="body2" color={colors.grey[300]} mb={2}>
                        Export email addresses for newsletter campaigns. Users are sorted by recent activity to help you target engaged users.
                    </Typography>
                    <Typography variant="body2" color={colors.grey[300]} mb={3}>
                        Current filter: <strong>{planFilter === 'all' ? 'All Users' : planFilter === 'premium' ? 'Premium Users' : 'Free Users'}</strong>
                        {totalUsers > 0 && <> • Total available: <strong>{totalUsers.toLocaleString()}</strong></>}
                    </Typography>

                    {/* Controls Row */}
                    <Grid container spacing={2} mb={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Activity Period</InputLabel>
                                <Select
                                    value={activityPeriod}
                                    onChange={(e) => setActivityPeriod(e.target.value)}
                                    label="Activity Period"
                                    disabled={previewLoading || loading}
                                >
                                    <MenuItem value="7d">Last 7 Days</MenuItem>
                                    <MenuItem value="30d">Last 30 Days</MenuItem>
                                    <MenuItem value="90d">Last 90 Days</MenuItem>
                                    <MenuItem value="all">All Time</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>User Type</InputLabel>
                                <Select
                                    value={activityType}
                                    onChange={(e) => setActivityType(e.target.value)}
                                    label="User Type"
                                    disabled={previewLoading || loading}
                                >
                                    <MenuItem value="all">All Users</MenuItem>
                                    <MenuItem value="active_only">Active Only</MenuItem>
                                    <MenuItem value="inactive_only">Inactive Only</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort Order</InputLabel>
                                <Select
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(e.target.value)}
                                    label="Sort Order"
                                    disabled={previewLoading || loading}
                                >
                                    <MenuItem value="desc">Most Active First</MenuItem>
                                    <MenuItem value="asc">Least Active First</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Number to Export"
                                value={emailCount}
                                onChange={(e) => setEmailCount(parseInt(e.target.value) || '')}
                                inputProps={{ min: 1, max: 100000 }}
                                error={!!error}
                                helperText={error || `Max: ${totalUsers.toLocaleString()}`}
                                disabled={loading || previewLoading}
                            />
                        </Grid>
                    </Grid>

                    {/* Preview Table */}
                    <Box>
                        <Typography variant="subtitle2" color={colors.grey[100]} mb={1}>
                            Preview (Top 100)
                        </Typography>
                        {previewLoading ? (
                            <Box display="flex" justifyContent="center" p={4}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400, 
                                    bgcolor: '#ffffffff',
                                    '& .MuiTableCell-root': {
                                        borderColor: '#ddd'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: colors.blueAccent[700], color: colors.grey[100], fontWeight: 'bold' }}>
                                                #
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: colors.blueAccent[700], color: colors.grey[100], fontWeight: 'bold' }}>
                                                Name
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: colors.blueAccent[700], color: colors.grey[100], fontWeight: 'bold' }}>
                                                Email
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: colors.blueAccent[700], color: colors.grey[100], fontWeight: 'bold' }} align="center">
                                                Activity
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: colors.blueAccent[700], color: colors.grey[100], fontWeight: 'bold' }} align="right">
                                                Last Active
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewData.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ color: colors.grey[300] }}>
                                                    No users found
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            previewData.map((user, index) => (
                                                <TableRow 
                                                    key={index} 
                                                    hover
                                                    sx={{
                                                        bgcolor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                                                        '&:hover': {
                                                            bgcolor: '#e3f2fd'
                                                        }
                                                    }}
                                                >
                                                    <TableCell sx={{ color: '#1e1e1e' }}>
                                                        {index + 1}
                                                    </TableCell>
                                                    <TableCell sx={{ color: '#1e1e1e', fontWeight: 500 }}>
                                                        {user.name || '-'}
                                                    </TableCell>
                                                    <TableCell sx={{ color: '#1e1e1e' }}>
                                                        {user.email}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip 
                                                            label={user.activity_count.toLocaleString()} 
                                                            size="small"
                                                            sx={{ 
                                                                bgcolor: user.activity_count > 0 ? colors.greenAccent[600] : colors.grey[700],
                                                                color: colors.grey[100],
                                                                fontWeight: 'bold'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: '#1e1e1e', fontSize: '0.85rem' }}>
                                                        {formatLastActive(user.last_active)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: colors.primary[400], p: 2 }}>
                <Button 
                    onClick={handleClose} 
                    disabled={loading || previewLoading}
                    sx={{ color: colors.grey[300] }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleExport}
                    variant="contained"
                    disabled={loading || previewLoading || previewData.length === 0}
                    sx={{ bgcolor: colors.blueAccent[700] }}
                >
                    {loading ? <CircularProgress size={24} /> : `Export ${emailCount > totalUsers ? totalUsers : emailCount} Emails`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportEmailsDialog;
